import { validZone } from "./teamText";

// Hora local ↔ instante en una zona IANA, como TimeZoneInfo del portal actual: una hora que no existe (se adelanta el
// reloj) es inválida; una que existe dos veces (se atrasa) se elige con su desfase.

/** Desfase de la zona en minutos en un instante (UTC = 0, Lima = −300). */
export function offsetAt(zone: string, utcMs: number): number {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: validZone(zone),
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    second: "numeric",
    hourCycle: "h23",
  }).formatToParts(new Date(utcMs));
  const get = (type: string) => Number(parts.find((part) => part.type === type)?.value ?? 0);
  const wall = Date.UTC(get("year"), get("month") - 1, get("day"), get("hour") % 24, get("minute"), get("second"));
  return Math.round((wall - Math.floor(utcMs / 1000) * 1000) / 60000);
}

/** Desfases posibles de una hora local (aaaa, mes 1–12, día, minutos del día): 0 inválida, 1 normal, 2 ambigua. */
export function localOffsets(zone: string, year: number, month: number, day: number, minute: number): number[] {
  const wall = Date.UTC(year, month - 1, day, 0, minute);
  const candidates = new Set([
    offsetAt(zone, wall - 12 * 3600_000),
    offsetAt(zone, wall + 12 * 3600_000),
    offsetAt(zone, wall),
  ]);
  return [...candidates].filter((offset) => offsetAt(zone, wall - offset * 60000) === offset).sort((a, b) => b - a);
}

/** El instante (ISO UTC) de una hora local con el desfase elegido si es ambigua; null si no existe o falta elegir. */
export function toInstant(
  zone: string,
  year: number,
  month: number,
  day: number,
  minute: number,
  chosenOffset: string,
): string | null {
  const offsets = localOffsets(zone, year, month, day, minute);
  if (offsets.length === 0) return null;
  let offset = offsets[0]!;
  if (offsets.length > 1) {
    const chosen = Number(chosenOffset);
    if (chosenOffset === "" || !offsets.includes(chosen)) return null;
    offset = chosen;
  }
  return new Date(Date.UTC(year, month - 1, day, 0, minute) - offset * 60000).toISOString();
}

/** «UTC −05:00». */
export function offsetLabel(minutes: number): string {
  const absolute = Math.abs(minutes);
  return `UTC ${minutes < 0 ? "−" : "+"}${String(Math.trunc(absolute / 60)).padStart(2, "0")}:${String(absolute % 60).padStart(2, "0")}`;
}

/** Fecha de hoy (aaaa-mm-dd) en la zona. */
export function todayIn(zone: string): { year: number; month: number; day: number } {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: validZone(zone),
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());
  const get = (type: string) => Number(parts.find((part) => part.type === type)?.value ?? 0);
  return { year: get("year"), month: get("month"), day: get("day") };
}

export type DateOnly = { year: number; month: number; day: number };
export const addDays = (date: DateOnly, days: number): DateOnly => {
  const value = new Date(Date.UTC(date.year, date.month - 1, date.day + days));
  return { year: value.getUTCFullYear(), month: value.getUTCMonth() + 1, day: value.getUTCDate() };
};
export const compareDates = (a: DateOnly, b: DateOnly) => a.year - b.year || a.month - b.month || a.day - b.day;
export const sameDate = (a: DateOnly, b: DateOnly) => compareDates(a, b) === 0;

/** «HH:mm» o «HH:mm:ss» → minutos (−1 si no es una hora). */
export function minuteOf(value: string): number {
  const match = /^(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(value);
  if (!match) return -1;
  const hour = Number(match[1]),
    minute = Number(match[2]);
  return hour < 24 && minute < 60 ? hour * 60 + minute : -1;
}
export const hourOf = (minute: number) =>
  `${String(Math.trunc(minute / 60)).padStart(2, "0")}:${String(minute % 60).padStart(2, "0")}`;
